import { DeleteOutlined, EmailOutlined, PhoneOutlined } from '@mui/icons-material';
import { Button, capitalize, IconButton, Stack, Typography } from '@mui/material';
import { getFullName } from '@provision/formatting-utils';
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_RowVirtualizer,
  MRT_TableOptions,
  useMaterialReactTable,
} from 'material-react-table';
import { SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FormattedPhoneInput,
  formatToPhoneNumber,
  MIN_NUMBER_LENGTH,
  Tab,
  Tabs,
  validateEmail,
} from 'src/components';
import { EMAIL_FIELD_PLACEHOLDER, PHONE_FIELD_PLACEHOLDER } from 'src/components/Forms/const';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { useGlobalStore } from 'src/store';
import { useScrollPaginateHandler } from 'src/utils/hooks';
import {
  BlockedContact,
  BlockedContactType,
  BlockedContactTypeFilter,
  useBlockedContacts,
} from './use-blocked-contacts';

// once the user has scrolled within 300px of the bottom of the table, fetch more data if we can.
// 300 is a good number that will give some space to avoid long waitings for the users
const SCROLL_OFFSET_PX = 300;

export const BlockedEmailsTable = () => {
  const [globalFilter, setGlobalFilter] = useState('');

  const [filterType, setFilterType] = useState<BlockedContactTypeFilter>('all');

  const [selectedEditingType, setSelectedEditingType] = useState<BlockedContactType>('email');

  const onFilterTypeChange = useCallback(
    (_: SyntheticEvent, value: BlockedContactTypeFilter) => {
      setFilterType(value);
      if (value !== 'all') {
        setSelectedEditingType(value);
      }
    },
    [setFilterType, setSelectedEditingType],
  );

  const {
    blockedContacts,
    createBlockedContact,
    deleteBlockedContact,
    hasMore,
    isLoading,
    isSaving,
    loadMore,
  } = useBlockedContacts(filterType, globalFilter);

  const currentPartnerId = useGlobalStore.use.selectedPartnerId();

  const [validationErrors, setValidationErrors] = useState<
    // Nested struct to represent rowId.cellId as a value.
    Record<string, Record<string, string | undefined> | undefined>
  >({});

  const submitCreateBlockedContact = useCallback(
    async (value: string, type: BlockedContactType) => {
      await createBlockedContact(type, value.trim().toLowerCase());
    },
    [createBlockedContact],
  );

  const handleOnCreateRow = useCallback<
    Required<MRT_TableOptions<BlockedContact>>['onCreatingRowSave']
  >(
    async ({ values, exitCreatingMode, row }) => {
      const value = values.value as string;
      const type = values.type as BlockedContactType;
      let validationError: string | undefined;
      if (!type) {
        setValidationErrors({
          ...validationErrors,
          [row.id]: {
            ...validationErrors[row.id],
            ['type']: 'Contact type is not selected',
          },
        });
        return;
      }
      if (type === 'email') {
        validationError = !validateEmail(value) ? 'Invalid email address' : undefined;
      } else if (type === 'phone') {
        validationError = !validatePhoneNumber(value) ? 'Invalid phone number' : undefined;
      }
      setValidationErrors({
        ...validationErrors,
        [row.id]: {
          ...validationErrors[row.id],
          ['value']: validationError,
        },
      });
      if (validationError) {
        return;
      }
      await submitCreateBlockedContact(value, row.original.type);
      exitCreatingMode();
    },
    [submitCreateBlockedContact, validationErrors],
  );

  const handleOnDeleteRow = useCallback(
    (blockedContact: BlockedContact) => async () => {
      await deleteBlockedContact(blockedContact.type, blockedContact.id);
    },
    [deleteBlockedContact],
  );

  const getContactTextPlaceholderByType = useCallback((type: BlockedContactType): string => {
    switch (type) {
      case 'phone':
        return PHONE_FIELD_PLACEHOLDER;
      case 'email':
        return EMAIL_FIELD_PLACEHOLDER;
      default:
        return '';
    }
  }, []);

  const columns = useMemo(
    (): MRT_ColumnDef<BlockedContact>[] => [
      {
        accessorKey: 'type',
        header: 'Type',
        grow: false,
        Cell: ({ row }) => (
          <Stack direction="row" spacing={1} alignItems="center">
            {row.original.type === 'email' ? (
              <EmailOutlined fontSize="small" />
            ) : (
              <PhoneOutlined fontSize="small" />
            )}
            <Typography>{capitalize(row.original.type)}</Typography>
          </Stack>
        ),
        muiEditTextFieldProps: ({ row }) => ({
          required: true,
          error: !!validationErrors?.[row.id]?.type,
          defaultValue: 'email',
          value: selectedEditingType,
          onChange: (event) => {
            // This is a hacky way to force Type column being re-rendered.
            setSelectedEditingType(event.target.value as BlockedContactType);
          },
        }),
        editVariant: 'select',
        editSelectOptions: [
          { label: 'Email', value: 'email' },
          { label: 'Phone', value: 'phone' },
        ],
      },
      {
        accessorKey: 'value',
        header: 'Value',
        Cell: ({ row }) => {
          let value = row.original.value;
          if (row.getValue('type') === 'phone') {
            value = formatToPhoneNumber(value);
          }
          return <Typography>{value}</Typography>;
        },
        editVariant: 'text',
        muiEditTextFieldProps: ({ table, row }) => ({
          required: true,
          error: !!validationErrors?.[row.id]?.value,
          helperText: validationErrors?.[row.id]?.value,
          placeholder: getContactTextPlaceholderByType(selectedEditingType),
          onBlur: async (event) => {
            const { creatingRow } = table.getState();
            const value = event.target.value;
            const type = selectedEditingType;
            if (creatingRow) {
              // Validate fields:
              // - type field should be selected
              // - validate Email format
              // - validate Phone format
              let validationError: string | undefined;
              if (!type) {
                setValidationErrors({
                  ...validationErrors,
                  [row.id]: {
                    ['type']: 'Contact type is not selected',
                  },
                });
                return;
              }
              if (type === 'email') {
                validationError = !validateEmail(value) ? 'Invalid email address' : undefined;
              } else if (type === 'phone') {
                validationError = !validatePhoneNumber(value)
                  ? 'Invalid phone number'
                  : undefined;
              }
              setValidationErrors({
                ...validationErrors,
                [row.id]: {
                  ['value']: validationError,
                },
              });
              if (validationError) {
                return;
              }
              await submitCreateBlockedContact(value, type);
              table.setCreatingRow(null);
            }
          },
          InputProps: {
            inputComponent: selectedEditingType === 'phone' ? FormattedPhoneInput : undefined,
          },
          inputProps: {
            style: { textTransform: 'lowercase' },
          },
        }),
      },
      {
        accessorKey: 'addedBy',
        header: 'Added By',
        enableEditing: false,
        enableGlobalFilter: false,
        grow: false,
        Cell: ({ row }) => <Typography>{getFullName(row.original.addedBy)}</Typography>,
        enableSorting: true,
      },
      {
        accessorKey: 'dateAdded',
        header: 'Date Added',
        enableEditing: false,
        enableGlobalFilter: false,
        grow: false,
        Cell: ({ row }) => (
          <Typography>
            {row.original.dateAdded
              ? new Date(row.original.dateAdded).toLocaleDateString()
              : ''}
          </Typography>
        ),
        enableSorting: true,
        sortingFn: (rowA, rowB) => {
          return (
            new Date(rowA.original.dateAdded).getTime() -
            new Date(rowB.original.dateAdded).getTime()
          );
        },
        sortDescFirst: true,
      },
    ],
    [
      getContactTextPlaceholderByType,
      submitCreateBlockedContact,
      validationErrors,
      selectedEditingType,
    ],
  );

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const rowVirtualizerInstanceRef = useRef<MRT_RowVirtualizer>(null);

  // https://www.material-react-table.com/docs/examples/infinite-scrolling
  useEffect(() => {
    if (!tableContainerRef.current) {
      return;
    }

    const { scrollHeight, scrollTop, clientHeight } = tableContainerRef.current;
    if (scrollHeight - scrollTop - clientHeight < SCROLL_OFFSET_PX && !isLoading && hasMore) {
      void loadMore();
    }
  }, [hasMore, isLoading, loadMore]);

  const handleScroll = useScrollPaginateHandler({
    loadMore,
    enabled: hasMore,
    thresholdPx: 800,
  });

  const config = useMemo((): MRT_TableOptions<BlockedContact> => {
    return {
      data: blockedContacts,
      columns,
      getRowId: (row) => row.id,
      positionActionsColumn: 'last',
      enableRowActions: true,
      renderRowActions: ({ row }) =>
        /* Don't allow deleting records that belong to platform partner */
        (row.original.partnerId !== PLATFORM_PARTNER_ID ||
          currentPartnerId === PLATFORM_PARTNER_ID) && (
          <Stack direction="row" spacing={1}>
            <IconButton
              aria-label="delete"
              color="error"
              onClick={handleOnDeleteRow(row.original)}
            >
              <DeleteOutlined />
            </IconButton>
          </Stack>
        ),
      enableRowVirtualization: true,
      enableColumnVirtualization: true,
      defaultColumn: {
        enableSorting: true,
        enableColumnActions: false,
      },
      enableBottomToolbar: false,
      state: {
        showProgressBars: isLoading,
        isLoading,
        isSaving,
        globalFilter,
      },
      initialState: {
        sorting: [
          {
            id: 'dateAdded',
            desc: true,
          },
        ],
      },
      enableGlobalFilter: true,
      globalFilterFn: 'contains',
      manualFiltering: true,
      onGlobalFilterChange: setGlobalFilter,
      muiSearchTextFieldProps: {
        placeholder: 'Filter results',
      },
      enableColumnFilters: false,
      muiTableContainerProps: {
        ref: tableContainerRef,
        onScroll: handleScroll,
        sx: {
          // Clamp function is modification of original one (`clamp(350px, 0px + 100vh, 9999px)`).
          // It's changed to occupy 100% of space instead of 100% of view port.
          maxHeight: 'clamp(350px, calc(100vh - 200px), 9999px)',
        },
      },
      muiTableHeadCellProps: {
        sx: (theme) => ({
          backgroundColor: theme.palette.custom.OV.sys.surfaceContainerLowest,
        }),
      },
      muiTablePaperProps: {
        elevation: 0,
        sx: {
          height: '100%',
        },
      },
      enableStickyHeader: true,
      rowVirtualizerInstanceRef,
      rowVirtualizerOptions: { overscan: 4 },
      enableSorting: true,
      enableFullScreenToggle: false,
      enableDensityToggle: false,
      enableHiding: false,
      enablePagination: false,
      enableEditing: true,
      editDisplayMode: 'row',
      positionCreatingRow: 'top',
      createDisplayMode: 'row',
      onCreatingRowSave: handleOnCreateRow,
      onCreatingRowCancel: () => setValidationErrors({}),
      renderTopToolbarCustomActions: () => (
        <Tabs value={filterType} onChange={onFilterTypeChange}>
          <Tab label="All Contacts" value="all" />
          <Tab label="Emails" value="email" />
          <Tab label="Phone Numbers" value="phone" />
        </Tabs>
      ),
    };
  }, [
    blockedContacts,
    columns,
    isLoading,
    isSaving,
    globalFilter,
    handleScroll,
    handleOnCreateRow,
    currentPartnerId,
    handleOnDeleteRow,
    filterType,
    onFilterTypeChange,
  ]);

  const table = useMaterialReactTable(config);

  return (
    <Stack spacing={1}>
      <Stack direction="row" justifyContent="right">
        <Button
          variant="contained"
          onClick={() => {
            table.setCreatingRow(true);
          }}
        >
          Block New Contact
        </Button>
      </Stack>
      <MaterialReactTable table={table} />
    </Stack>
  );
};

// validatePhoneNumber is a custom validation to comply to the limitations in `blocked_phone_number` table.
function validatePhoneNumber(value?: string): boolean {
  return value?.length === MIN_NUMBER_LENGTH;
}
